import React, { createContext, useState, useContext, useRef, RefObject, useMemo } from "react";

import {
  AvailablePaymentCurrency,
  DonationFormCategory,
  getCurrencyFromCountry,
  OrganizationCountry,
  TicketingFormCategory,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";

import { useIsOnboarding } from "../../../hooks/useIsOnboarding";
import { useStepNavigation } from "../Navigation/useStepNavigation";
import { LiveEditorStep, LiveEditorStepKey } from "../Steps/LiveEditorStep.enum";

import { useCurrentUserContext } from "@/contexts/CurrentUserContext";
import { FormV2StepKey } from "@/features/FormV2/types/FormV2StepKey";
import { useTrackAndSaveMostAdvancedStepReached } from "@/features/LiveFormEditor/Navigation/useTrackAndSaveMostAdvancedStepReached";
import { LiveDonationData, LiveTicketingData } from "@/features/LiveFormEditor/types/LiveFormEditor.types";
import { DonationFormWithUserOrOrgAccessOutput, TicketingOutput } from "@/types/trpc";

export type PreviewType = "form" | "email" | "e-ticket" | "tax-receipt" | "membership-card";

export type SaveFormModalState = {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  closeModalAndExecuteActions: () => void;
};

export type ShareFormModalState = {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
};

export type CompleteGhostSignUpDialogState = {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
};

export type FormType = "ticketing" | "donation-form";

export interface LiveFormEditorContextType<FormTypeDiscriminator extends FormType = FormType> {
  currentStepIndex: LiveEditorStep;
  currentStepKey: LiveEditorStepKey;
  updateCurrentStep: (stepKey: LiveEditorStepKey) => void;
  totalNumberOfSteps: number;
  liveEditorStepsKeys: LiveEditorStepKey[];
  getIndexStepFromKey: (key: LiveEditorStepKey) => LiveEditorStep;
  getKeyStepFromIndex: (index: LiveEditorStep) => LiveEditorStepKey;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  mostAdvancedStepReached: LiveEditorStep;
  apiFormObject: FormTypeDiscriminator extends "ticketing" ? TicketingOutput : DonationFormWithUserOrOrgAccessOutput;
  updateApiFormObject: (
    formObject: FormTypeDiscriminator extends "ticketing" ? TicketingOutput : DonationFormWithUserOrOrgAccessOutput
  ) => void;
  liveFormData?: FormTypeDiscriminator extends "ticketing" ? LiveTicketingData : LiveDonationData;
  setLiveFormData: (data: FormTypeDiscriminator extends "ticketing" ? LiveTicketingData : LiveDonationData) => void;
  previewDevice: "mobile" | "desktop";
  setPreviewDevice: (mode: "mobile" | "desktop") => void;
  setRefreshArrowPosition: (callback: () => void) => void;
  refreshArrowPosition: () => void;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  previewType: PreviewType;
  saveCurrentStep: RefObject<() => Promise<void>>;
  isStepChanging: boolean;
  setIsStepChanging: (isStepChanging: boolean) => void;
  editOrCreateMode: "edit" | "create";
  setEditOrCreateMode: (mode: "edit" | "create") => void;
  checkIfFormIsDirty: RefObject<() => boolean>;
  resetForm: RefObject<() => void>;
  isNavigationOpen: boolean;
  setIsNavigationOpen: (isNavigationOpen: boolean) => void;
  isOnboarding: boolean;
  saveFormModalState: SaveFormModalState;
  shareFormModalState: ShareFormModalState;
  askSaveIfDirtyAndExecute: (callback: () => void) => void;
  formType: FormTypeDiscriminator extends "ticketing" ? "ticketing" : "donation-form";
  formCategory: FormTypeDiscriminator extends "ticketing" ? TicketingFormCategory : DonationFormCategory;
  formCurrency: AvailablePaymentCurrency;
  isTicketingClosed?: boolean;
  previewStepKey: FormV2StepKey;
  setPreviewStepKey: (stepKey: FormV2StepKey) => void;
  completeGhostSignUpDialogState: CompleteGhostSignUpDialogState;
  isGhostUser: boolean;
  isInviteCollaboratorsStepVisible: boolean;
}

const defaultValues: LiveFormEditorContextType<"ticketing"> = {
  currentStepIndex: 0,
  currentStepKey: "title",
  updateCurrentStep: () => {},
  totalNumberOfSteps: 0,
  liveEditorStepsKeys: [],
  goToNextStep: () => {},
  goToPreviousStep: () => {},
  mostAdvancedStepReached: 1,
  getIndexStepFromKey: () => 0,
  getKeyStepFromIndex: () => "title",
  apiFormObject: {} as TicketingOutput,
  updateApiFormObject: () => {},
  liveFormData: undefined,
  setLiveFormData: () => {},
  previewDevice: "mobile",
  setPreviewDevice: () => {},
  setRefreshArrowPosition: () => {},
  refreshArrowPosition: () => {},
  iframeRef: { current: null },
  previewType: "form",
  saveCurrentStep: { current: () => Promise.resolve() },
  isStepChanging: false,
  setIsStepChanging: () => {},
  editOrCreateMode: "edit",
  setEditOrCreateMode: () => {},
  saveFormModalState: {
    isOpen: false,
    openModal: () => {},
    closeModal: () => {},
    closeModalAndExecuteActions: () => {},
  },
  shareFormModalState: {
    isOpen: false,
    openModal: () => {},
    closeModal: () => {},
  },
  checkIfFormIsDirty: { current: () => false },
  resetForm: { current: () => {} },
  isNavigationOpen: false,
  setIsNavigationOpen: () => {},
  isOnboarding: false,
  askSaveIfDirtyAndExecute: () => {},
  formType: "ticketing",
  formCategory: TicketingFormCategory.Event,
  formCurrency: AvailablePaymentCurrency.Usd,
  previewStepKey: "selection",
  setPreviewStepKey: () => {},
  completeGhostSignUpDialogState: {
    isOpen: false,
    openModal: () => {},
    closeModal: () => {},
  },
  isGhostUser: false,
  isInviteCollaboratorsStepVisible: false,
};

export const LiveFormEditorContext = createContext<LiveFormEditorContextType<FormType>>(
  defaultValues as LiveFormEditorContextType<FormType>
);

interface LiveFormEditorProviderProps<F extends FormType> {
  formType: F;
  steps: { key: LiveEditorStepKey; shouldBeSkip?: boolean }[];
  liveFormData: F extends "ticketing" ? LiveTicketingData | null : LiveDonationData | null;
  apiFormObject: F extends "ticketing" ? TicketingOutput : DonationFormWithUserOrOrgAccessOutput;
  editOrCreateMode: "edit" | "create";
  setLiveFormData: (data: F extends "ticketing" ? LiveTicketingData : LiveDonationData) => void;
  setApiFormObject: (
    formObject: F extends "ticketing" ? TicketingOutput : DonationFormWithUserOrOrgAccessOutput
  ) => void;
  setEditOrCreateMode: (mode: "edit" | "create") => void;
  children: (params: LiveFormEditorContextType<F>) => React.ReactNode;
  formCategory: F extends "ticketing" ? TicketingFormCategory : DonationFormCategory;
  isTicketingClosed?: boolean;
  isInviteCollaboratorsStepVisible: boolean;
}

export const LiveFormEditorProvider = <F extends FormType>({
  children,
  steps,
  liveFormData,
  apiFormObject,
  editOrCreateMode,
  setLiveFormData,
  setApiFormObject,
  setEditOrCreateMode,
  formType,
  formCategory,
  isTicketingClosed,
  isInviteCollaboratorsStepVisible,
}: LiveFormEditorProviderProps<F>) => {
  const {
    currentStepIndex,
    currentStepKey,
    updateCurrentStep,
    goToNextStep,
    goToPreviousStep,
    getIndexStepFromKey,
    getKeyStepFromIndex,
    liveEditorStepsKeys,
  } = useStepNavigation(steps, apiFormObject);
  const { mostAdvancedStepReached } = useTrackAndSaveMostAdvancedStepReached({
    currentStepIndex,
    apiFormObject,
    currentStepKey,
    getIndexStepFromKey,
    getKeyStepFromIndex,
    formType,
  });
  const { currentUser } = useCurrentUserContext();
  const searchParams = useSearchParams();

  const formCurrency = useMemo(
    () => getCurrencyFromCountry(apiFormObject.organization?.country || OrganizationCountry.UnitedStates),
    [apiFormObject.organization?.country]
  );

  const [previewDevice, setPreviewDevice] = useState<"mobile" | "desktop">("mobile");
  const [refreshArrowPosition, setRefreshArrowPosition] = useState<() => void>(() => {});
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const saveCurrentStep = useRef<() => Promise<void>>(() => Promise.resolve());
  const checkIfFormIsDirty = useRef<() => boolean>(() => false);
  const resetForm = useRef<() => void>(() => {});

  const [isNavigationOpen, setIsNavigationOpen] = useState(true);

  const previewType = getPreviewType(currentStepKey, formCategory);

  const totalNumberOfSteps = liveEditorStepsKeys.length;

  const { isOnboarding } = useIsOnboarding();

  const [isSaveFormModalOpen, setIsSaveFormModalOpen] = useState(false);
  const saveCallbackRef = useRef<() => void>(() => {});
  const saveFormModalState: SaveFormModalState = {
    isOpen: isSaveFormModalOpen,
    openModal: () => setIsSaveFormModalOpen(true),
    closeModal: () => setIsSaveFormModalOpen(false),
    closeModalAndExecuteActions: () => {
      setIsSaveFormModalOpen(false);
      saveCallbackRef.current();
      saveCallbackRef.current = () => {};
    },
  };
  const askSaveIfDirtyAndExecute = (callback: () => void) => {
    // If the form is dirty, we open the save modal to ask confirmation to save the changes before executing the callback.
    if (checkIfFormIsDirty.current()) {
      saveCallbackRef.current = callback;
      setIsSaveFormModalOpen(true);
    } else {
      // If the form is not dirty, we directly execute the callback without opening the save modal.
      callback();
    }
  };

  const [isShareFormModalOpen, setIsShareFormModalOpen] = useState(false);
  const shareFormModalState: ShareFormModalState = {
    isOpen: isShareFormModalOpen,
    openModal: () => setIsShareFormModalOpen(true),
    closeModal: () => setIsShareFormModalOpen(false),
  };

  const [isCompleteGhostSignUpDialogOpen, setIsCompleteGhostSignUpDialogOpen] = useState<boolean>(
    Boolean(searchParams.get("signUpStep"))
  );

  const completeGhostSignUpDialogState: CompleteGhostSignUpDialogState = {
    isOpen: isCompleteGhostSignUpDialogOpen,
    openModal: () => setIsCompleteGhostSignUpDialogOpen(true),
    closeModal: () => {
      setIsCompleteGhostSignUpDialogOpen(false);
    },
  };

  const [isStepChanging, setIsStepChanging] = useState(false);

  const [previewStepKey, setPreviewStepKey] = useState<FormV2StepKey>("selection");

  const contextValue: LiveFormEditorContextType<F> = {
    currentStepIndex,
    currentStepKey,
    updateCurrentStep,
    goToNextStep,
    goToPreviousStep,
    mostAdvancedStepReached,
    liveEditorStepsKeys,
    apiFormObject,
    updateApiFormObject: setApiFormObject,
    liveFormData: liveFormData as F extends "ticketing" ? LiveTicketingData : LiveDonationData,
    setLiveFormData,
    previewDevice,
    setPreviewDevice,
    setRefreshArrowPosition,
    refreshArrowPosition,
    iframeRef,
    previewType,
    saveCurrentStep,
    isStepChanging,
    setIsStepChanging,
    editOrCreateMode,
    setEditOrCreateMode,
    saveFormModalState,
    shareFormModalState,
    checkIfFormIsDirty,
    resetForm,
    isNavigationOpen,
    setIsNavigationOpen,
    totalNumberOfSteps,
    isOnboarding,
    askSaveIfDirtyAndExecute,
    getIndexStepFromKey,
    getKeyStepFromIndex,
    formType: formType as never,
    formCategory,
    formCurrency,
    isTicketingClosed,
    previewStepKey,
    setPreviewStepKey,
    isGhostUser: currentUser?.isGhostUser || false,
    completeGhostSignUpDialogState,
    isInviteCollaboratorsStepVisible,
  };

  return <LiveFormEditorContext.Provider value={contextValue}>{children(contextValue)}</LiveFormEditorContext.Provider>;
};

export const useLiveFormEditorContext = <F extends FormType>() => {
  return useContext(LiveFormEditorContext) as LiveFormEditorContextType<F>;
};

const getPreviewType = (
  currentStepKey: LiveEditorStepKey,
  formCategory: TicketingFormCategory | DonationFormCategory
): PreviewType => {
  if (
    currentStepKey === "thank-you-email" ||
    currentStepKey === "thank-you-email-not-winner" ||
    currentStepKey === "thank-you-email-fundraiser"
  ) {
    return "email";
  }
  if (currentStepKey === "e-ticket") {
    if (formCategory === TicketingFormCategory.MembershipV2) {
      return "membership-card";
    }
    return "e-ticket";
  }
  if (currentStepKey === "tax-receipts") {
    return "tax-receipt";
  }
  return "form";
};
